/**
 * Account remaining-quota helpers. UI copy stays in translations.
 */

/**
 * Unused slots in a capped meter. Null limit means unlimited (Pro).
 *
 * @param {number|null|undefined} used
 * @param {number|null|undefined} limit
 * @returns {number|null}
 */
export const leftover = (used, limit) => {
  if (limit == null) {
    return null;
  }
  const n = Number(limit) - Number(used || 0);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

/**
 * Free-plan leftovers and one-shot credits from GET /api/cvs/stats/ quota.
 *
 * @param {object|null|undefined} quota
 * @returns {{ atsLeft: number, studioLeft: number, pdfCredits: number, atsCredits: number }}
 */
export const freeRemainders = (quota) => ({
  atsLeft: leftover(quota?.analyses?.used, quota?.analyses?.limit ?? 1) ?? 0,
  studioLeft: leftover(quota?.studio_extracts?.used, quota?.studio_extracts?.limit ?? 1) ?? 0,
  pdfCredits: Number(quota?.pdf_credits) || 0,
  atsCredits: Number(quota?.ats_credits) || 0,
});

/**
 * Which remaining sentence to show for a Free user.
 *
 * @param {{ atsLeft: number, studioLeft: number }} remainders
 * @returns {'both'|'ats'|'studio'|'none'}
 */
export const remainingCopyId = ({ atsLeft, studioLeft }) => {
  if (atsLeft > 0 && studioLeft > 0) {
    return 'both';
  }
  if (atsLeft > 0) {
    return 'ats';
  }
  if (studioLeft > 0) {
    return 'studio';
  }
  return 'none';
};

/**
 * Heading for a saved CV row: target job first, else the stored title.
 *
 * @param {{ job_position?: string, title?: string }|null|undefined} item
 * @returns {string}
 */
export const libraryHeading = (item) => {
  const job = String(item?.job_position || '').trim();
  if (job) {
    return job;
  }
  return String(item?.title || '').trim();
};

/**
 * Document kind for Account cards.
 *
 * @param {{ kind?: string, has_analysis?: boolean }|null|undefined} item
 * @returns {'report'|'download'|'upload'}
 */
export const workKind = (item) => {
  const kind = String(item?.kind || '').trim();
  if (kind === 'report' || kind === 'download' || kind === 'upload') {
    return kind;
  }
  if (item?.has_analysis) {
    return 'report';
  }
  return 'upload';
};

/**
 * ATS score band used on Account document cards.
 *
 * @param {number|null|undefined} score
 * @returns {'excellent'|'solid'|'fair'|'weak'}
 */
export const scoreBand = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) {
    return 'fair';
  }
  if (n >= 80) {
    return 'excellent';
  }
  if (n >= 65) {
    return 'solid';
  }
  if (n >= 45) {
    return 'fair';
  }
  return 'weak';
};

/**
 * Editor path to make another file after a download (PDF is not stored).
 *
 * @param {string} flow
 * @param {{ keepLook: string, rewrite: string, editor: string, fromFile: string }} paths
 * @returns {string}
 */
export const downloadResumePath = (flow, paths) => {
  if (flow === 'keep_look') {
    return paths.keepLook;
  }
  if (flow === 'from_scratch') {
    return paths.editor;
  }
  if (flow === 'ai_cv') {
    return paths.rewrite;
  }
  return paths.fromFile;
};

/**
 * Initials for the Account avatar (name, else email local-part).
 *
 * @param {string} label
 * @returns {string}
 */
export const accountInitials = (label) => {
  const text = String(label || '').trim();
  if (!text) {
    return '?';
  }
  const words = text.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return text.slice(0, 2).toUpperCase();
};
